import { getAuth } from "firebase/auth";
import { ShoppingList, ShoppingListItem } from "../types";
import type { ExtractRecipeResult } from "../types/recipe-import";

export interface CategorizeResult {
  success: boolean;
  items?: ShoppingListItem[];
  error?: string;
}

async function callGeminiApi<T>(action: string, payload: unknown): Promise<T> {
  const user = getAuth().currentUser;
  if (!user) {
    return { success: false, error: "You must be signed in to use this." } as T;
  }

  const idToken = await user.getIdToken();

  const response = await fetch("/api/gemini", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ action, payload }),
  });

  return response.json();
}

/**
 * Categorizes a shopping list via the server-side Gemini endpoint
 */
export async function categorizeShoppingList(
  shoppingList: ShoppingList,
): Promise<CategorizeResult> {
  try {
    return await callGeminiApi<CategorizeResult>("categorize", shoppingList);
  } catch (error) {
    console.error("Error categorizing shopping list:", error);
    return {
      success: false,
      error: "Failed to organize list. Please try again.",
    };
  }
}

/**
 * Extracts recipe data from an image via the server-side Gemini endpoint
 * @param imageData - Base64 encoded image data from ImageUploader
 */
export async function extractRecipeFromImage(
  imageData: string,
): Promise<ExtractRecipeResult> {
  try {
    return await callGeminiApi<ExtractRecipeResult>(
      "extractImage",
      imageData,
    );
  } catch (error) {
    console.error("Error extracting recipe from image:", error);
    return {
      success: false,
      error: "Failed to extract recipe. Please try again.",
    };
  }
}

/**
 * Extracts recipe data from text or a URL via the server-side Gemini endpoint
 */
export async function extractRecipeFromText(
  textInput: string,
): Promise<ExtractRecipeResult> {
  try {
    return await callGeminiApi<ExtractRecipeResult>("extractText", textInput);
  } catch (error) {
    console.error("Error extracting recipe from text:", error);
    return {
      success: false,
      error: "Failed to extract recipe. Please try again.",
    };
  }
}
